// Per-section bookkeeping for the global Save (runSaveConfig).
//
// One Save sends many independent requests. When some of them fail the save
// must not be all-or-nothing: what went through is finished (uploads, refetch,
// baseline) and only the sections with a failed request stay dirty. These
// helpers map each request to its section, reconcile a half-saved CRUD list so
// a retry repeats only what failed, and build the report the screen shows.

// Op `kind` → section key. The section keys are the ones the screen labels in
// SAVE_SECTION_META (WebshopConfigScreen) — one per dirty flag.
export const KIND_SECTION = {
    'container-del': 'containers', 'container-upd': 'containers', 'container-new': 'containers',
    'home-order': 'order',
    'show-landing': 'showLanding',
    'page404-order': 'order404',
    'grid': 'productGrid',
    'card': 'productCard',
    'style': 'styleSettings', 'font': 'styleSettings',
    'groups': 'groupsFilter',
    'widths': 'globalWidths',
    'header-logo': 'headerLogo',
    'nav': 'navigation',
    'navbar-order': 'navbarOrder',
    'footer': 'footer', 'extlink-create': 'footer', 'extlink-update': 'footer', 'extlink-delete': 'footer',
    'extra-footer': 'extraFooter', 'extra-footer-content': 'extraFooter',
    'static-page-content': 'staticPages',
    'card-content': 'cards',
    'card-setting': 'cards',
    'brands': 'brandsConfig', 'brands-json': 'brandsConfig',
    'scripts-head': 'overige', 'scripts-body': 'overige', 'tracking-tools': 'overige', 'cookie-consent': 'overige', 'tracking-events': 'overige',
    'cadeaubon': 'cadeaubonConfig',
    'plc-del': 'cadeaubonPlaceholders', 'plc-new': 'cadeaubonPlaceholders', 'plc-upd': 'cadeaubonPlaceholders',
    'lay-del': 'cadeaubonLayouts', 'lay-new': 'cadeaubonLayouts', 'lay-upd': 'cadeaubonLayouts',
    'processing': 'processing',
    'sync': 'sync',
    'ccg-del': 'customerGroups', 'ccg-new': 'customerGroups', 'ccg-upd': 'customerGroups',
    'looks': 'looks', 'looks-content': 'looks', 'looks-cta': 'looks',
    'img-gallery': 'imageGallery', 'img-gallery-json': 'imageGallery',
    'pdi': 'pdi', 'pdi-json': 'pdi',
    'carousel-create': 'carousel', 'carousel-update': 'carousel', 'carousel-delete': 'carousel', 'carousel-cfg': 'carousel',
    'imagegrid-create': 'imageGrid', 'imagegrid-update': 'imageGrid', 'imagegrid-delete': 'imageGrid', 'imagegrid-cfg': 'imageGrid',
    'map': 'map', 'map-style': 'map',
    'store': 'stores',
    'banner-del': 'banners', 'banner-new': 'banners', 'banner-upd': 'banners',
    'dynpage-del': 'dynamicPages', 'dynpage-new': 'dynamicPages', 'dynpage-upd': 'dynamicPages',
};

export const sectionOfOp = (op) => KIND_SECTION[op && op.kind] || 'other';

export const opSucceeded = (r) => !!(r && r.response && r.response.success);

// Why a request failed, as specific as we have it. The auth proxy answers every
// mini-api error with the same generic message and passes the shop's own
// status and {code, message} along: in `data` for everyone, and (older proxy,
// or with more detail) in `dev` for debug / super users. Prefer those.
export const failureMessage = (response) => {
    if (!response) return '';
    const info  = [response.data, response.dev].find(x => x && typeof x === 'object' && (x.status || x.body)) || null;
    const inner = info && info.body && typeof info.body === 'object' ? info.body.message : '';
    if (inner) return info.status ? `${info.status} — ${inner}` : String(inner);
    if (info && info.status) return `${info.status} — ${response.message || ''}`;
    return response.message || '';
};

// Row adapters for reconcileList. `baseOf` turns a saved row into its baseline
// form: a pending upload is NOT part of what the row request saved, so the
// baseline keeps the image as the server has it and the row stays dirty until
// the upload itself went through.
export const SERVER_ID_ROW = {
    idOf:   (r) => r.serverId,
    withId: (r, id) => ({ ...r, serverId: id }),
    baseOf: (row) => row,
};
export const PLAIN_ID_ROW = {
    idOf:   (r) => r.id,
    withId: (r, id) => ({ ...r, id }),
    baseOf: (row) => row,
};
// A created row may point at an image already on the server (a copy from
// another language — sent along with the create), so that one IS saved; only a
// picked file (`file`) is still to upload.
const savedImage = (img) => (img && !img.file ? img : null);
export const IMAGE_ROW = {
    ...SERVER_ID_ROW,
    baseOf: (row, old) => ({
        ...row,
        desktopImage:     old ? old.desktopImage     : savedImage(row.desktopImage),
        alternativeImage: old ? old.alternativeImage : savedImage(row.alternativeImage),
    }),
};
export const LAYOUT_ROW = {
    ...SERVER_ID_ROW,
    baseOf: (row) => ({ ...row, imageFile: null, layoutImageFile: null, designDirty: false }),
};

/**
 * Reconcile one CRUD list after a save in which some of its requests failed.
 *
 * Returns the local list with server ids on the rows that were created, and a
 * baseline that describes what the server now holds — in local order, because
 * the dirty checks compare the two lists position by position. A retry then
 * sends only what is still different: no second POST for a row that exists, no
 * DELETE for a row that is already gone.
 *
 * `opResults` are the dispatched ops (with `response`) carrying
 * `crud: { type: 'create' | 'update', row }` or `{ type: 'delete', id }`. Rows
 * are matched by object identity, so results of other lists are ignored.
 */
export const reconcileList = (local, baseline, opResults, { idOf, withId, baseOf }) => {
    const created     = new Map();   // local row → new server id
    const failedRows  = new Set();   // local rows whose create/update failed
    const updatedRows = new Set();   // local rows whose update went through
    const deleted     = new Set();   // server ids whose DELETE went through
    for (const r of opResults || []) {
        const c = r.crud;
        if (!c) continue;
        if (c.type === 'delete') {
            if (opSucceeded(r)) deleted.add(String(c.id));
        } else if (!opSucceeded(r)) {
            failedRows.add(c.row);
        } else if (c.type === 'create') {
            const id = r.response && r.response.data ? r.response.data.id : null;
            if (id != null) created.set(c.row, id);
            else failedRows.add(c.row);   // no id back → can't pin it; treat as not created
        } else {
            updatedRows.add(c.row);
        }
    }

    const oldById = new Map();
    for (const b of baseline || []) {
        if (idOf(b) != null) oldById.set(String(idOf(b)), b);
    }

    const nextLocal = [];
    const nextBase  = [];
    const seen      = new Set();
    for (const row of local || []) {
        const id = idOf(row);
        if (id != null) seen.add(String(id));
        // Flagged for removal and gone server-side → drop it here too.
        if (id != null && deleted.has(String(id))) continue;
        const old = id != null ? oldById.get(String(id)) : undefined;
        if (created.has(row)) {
            const withServerId = withId(row, created.get(row));
            nextLocal.push(withServerId);
            nextBase.push(baseOf(withServerId, undefined));
            continue;
        }
        nextLocal.push(row);
        if (failedRows.has(row)) {
            if (old) nextBase.push(old);           // failed update: still the old row on the server
            continue;                              // failed create: not on the server at all
        }
        if (updatedRows.has(row)) {
            nextBase.push(baseOf(row, old));
            continue;
        }
        if (old) nextBase.push(old);               // untouched (or only a pending upload)
    }
    // Removed locally but the DELETE failed → the row is still on the server.
    for (const b of baseline || []) {
        const id = idOf(b);
        if (id != null && !seen.has(String(id)) && !deleted.has(String(id))) nextBase.push(b);
    }
    return { local: nextLocal, baseline: nextBase };
};

/**
 * Build the report for the screen.
 *   dirty      { sectionKey: boolean }   sections that had changes going in
 *   failedOps  ops (with `response`) that did not succeed
 *   extra      [{ section, message, blockedBy? }]  failures without an op
 *   warnings   [{ section, text }]       e.g. an image that failed to upload
 * → { ok, saved: [key], failed: [{ section, blockedBy, errors: [{ method, path, message }] }], warnings }
 */
export const buildSaveReport = ({ dirty, failedOps = [], extra = [], warnings = [] }) => {
    const bySection = new Map();
    const entry = (section) => {
        if (!bySection.has(section)) bySection.set(section, { section, blockedBy: null, errors: [] });
        return bySection.get(section);
    };
    for (const f of failedOps) {
        entry(sectionOfOp(f)).errors.push({
            method:  f.method || 'PATCH',
            path:    f.path || '',
            message: failureMessage(f.response),
        });
    }
    for (const x of extra) {
        const e = entry(x.section);
        if (x.blockedBy) e.blockedBy = x.blockedBy;
        if (x.message) e.errors.push({ method: '', path: '', message: x.message });
    }
    const failed = Array.from(bySection.values());
    const saved  = Object.keys(dirty || {}).filter(k => dirty[k] && !bySection.has(k));
    return { ok: failed.length === 0, saved, failed, warnings };
};
